import { io, Socket } from 'socket.io-client'
import { useWebSocketStore } from './store'

export class SocketService {
  public socket: Socket | null = null
  private url: string
  private isConnecting: boolean = false
  private reconnectAttempts: number = 0
  private maxReconnectAttempts: number = 5
  private reconnectDelay: number = 1000
  private reconnectTimer: NodeJS.Timeout | null = null
  private listeners: Map<string, Set<(data: any) => void>> = new Map()

  constructor(url: string) {
    this.url = url
  }

  private getAdminToken(): string {
    return sessionStorage.getItem('impersonation_token')
      || sessionStorage.getItem('admin_token')
      || localStorage.getItem('admin_token')
      || ''
  }

  private getAgentToken(): string {
    return sessionStorage.getItem('agent_token')
      || localStorage.getItem('agent_token')
      || ''
  }

  private bindRegisteredListeners() {
    if (!this.socket) return

    this.listeners.forEach((callbacks, event) => {
      callbacks.forEach((callback) => {
        this.socket?.on(event, callback)
      })
    })
  }

  connect() {
    const wsStore = useWebSocketStore()

    // Protection contre les connexions multiples
    if (this.socket?.connected) {
      return
    }

    if (this.isConnecting) {
      return
    }

    wsStore.isHttpFallback = false // <-- mettre true pour tester le modal
    if (wsStore.isHttpFallback) {
      wsStore.enableHttpFallback()
      return
    }

    if (!wsStore.checkWebSocketSupport()) {
      return
    }


    this.isConnecting = true
    this.reconnectAttempts = 0

    try {
      const adminToken = this.getAdminToken()
      const agentToken = this.getAgentToken()

      if (import.meta.env.DEV) {
        console.info('[WebSocket] Connecting', {
          url: this.url,
          source: 'admin',
          hasAdminToken: Boolean(adminToken),
          hasAgentToken: Boolean(agentToken),
        })
      }

      this.socket = io(this.url, {
        query: {
          source: 'admin',
        },
        auth: {
          adminToken,
          agentToken,
        },
        transports: ['websocket'], // évite polling + 400 Bad Request
        reconnection: false, // géré manuellement pour meilleur contrôle
        timeout: 10000, // timeout de 10s
      })

      this.bindRegisteredListeners()

      this.socket.on('connect', () => {
        this.isConnecting = false
        this.reconnectAttempts = 0
        wsStore.setConnected(true)
        wsStore.setWebSocketSupported(true)

        if (import.meta.env.DEV) {
          console.info('[WebSocket] Connected', {
            id: this.socket?.id,
            transport: this.socket?.io.engine.transport.name,
          })
        }
      })

      this.socket.on('disconnect', (reason) => {
        this.isConnecting = false
        wsStore.setConnected(false)

        if (import.meta.env.DEV) {
          console.warn('[WebSocket] Disconnected', reason)
        }

        // Tentative de reconnexion automatique si déconnexion inattendue
        if (reason !== 'io client disconnect' && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.scheduleReconnect()
        }
      })

      this.socket.on('connect_error', (err) => {
        console.error('[WebSocket] Erreur de connexion:', err.message)
        this.isConnecting = false
        
        // Tentative de reconnexion automatique
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.scheduleReconnect()
        } else {
          console.error('[WebSocket] Nombre maximal de tentatives de reconnexion atteint')
          wsStore.enableHttpFallback()
        }
      })

      // Écoute des événements de debug
      // this.socket.onAny((eventName, ...args) => {
      //   console.log(`[WebSocket] Événement reçu: ${eventName}`, args)
      // })
      if (import.meta.env.DEV) {
        this.socket.onAny((eventName, ...args) => {
          console.info('[WebSocket] Event received:', eventName, args)
        })
      }
    } catch (error) {
      console.error('[WebSocket] Erreur lors de la création:', error)
      this.isConnecting = false
      wsStore.enableHttpFallback()
    }
  }

  private scheduleReconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
    }
    
    this.reconnectAttempts++
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1) // backoff exponentiel
    
    console.log(`[WebSocket] Tentative de reconnexion ${this.reconnectAttempts}/${this.maxReconnectAttempts} dans ${delay}ms`)
    
    this.reconnectTimer = setTimeout(() => {
      if (!this.socket?.connected && !this.isConnecting) {
        this.connect()
      }
    }, delay)
  }

  onMessage(event: string, callback: (data: any) => void) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }

    const callbacks = this.listeners.get(event)!
    const isNewCallback = !callbacks.has(callback)
    callbacks.add(callback)

    if (!this.socket) return
    if (isNewCallback) this.socket.on(event, callback)
  }

  send(event: string, data?: any) {
    if (!this.socket) return
    if (data === undefined) {
      this.socket.emit(event)
      return
    }
    this.socket.emit(event, data)
  }

  isConnected(): boolean {
    return this.socket?.connected ?? false
  }

  isConnectingStatus(): boolean {
    return this.isConnecting
  }

  getConnectionStatus(): 'disconnected' | 'connecting' | 'connected' {
    if (this.socket?.connected) return 'connected'
    if (this.isConnecting) return 'connecting'
    return 'disconnected'
  }

  off(event: string, callback: (data: any) => void) {
    this.listeners.get(event)?.delete(callback)
    if (!this.socket) return
    this.socket.off(event, callback)
  }

  disconnect() {
    
    // Annuler toute reconnexion programmée
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    
    this.isConnecting = false
    this.reconnectAttempts = 0
    
    if (this.socket) {
      this.socket.disconnect()
      this.socket = null
    }
    
    const wsStore = useWebSocketStore()
    wsStore.setConnected(false)
  }
}
